/**
 * Expands the header search over the navigation area.
 *
 * The open state lives on the sibling <details> toggle so it still works before
 * this script runs. This element only adds the dismiss behaviours that CSS
 * cannot express: clicking outside, pressing Escape, and the cancel button.
 */
class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.toggle = this.closest('.header')?.querySelector('.header__search-toggle-wrapper');
    this.input = this.querySelector('.header__search-input');

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleToggle = this.handleToggle.bind(this);

    this.querySelector('[data-search-cancel]')?.addEventListener('click', () => this.close());
    this.toggle?.addEventListener('toggle', this.handleToggle);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  get isOpen() {
    return this.toggle?.open ?? false;
  }

  handleToggle() {
    if (this.isOpen) this.input?.focus();
  }

  handleDocumentClick(event) {
    if (!this.isOpen) return;
    if (this.contains(event.target) || this.toggle?.contains(event.target)) return;

    this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  /**
   * Empties the field and the results under it.
   *
   * The `input` event is what predictive-search listens on, so dispatching it
   * lets that element tear its own panel down rather than reaching into it.
   */
  clearInput() {
    if (!this.input || this.input.value === '') return;

    this.input.value = '';
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  close() {
    if (!this.toggle) return;

    this.clearInput();
    this.toggle.open = false;
    this.toggle.querySelector('summary')?.focus();
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}
